import { useQuery, useMutation, useQueryClient, UseMutationResult } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useTenantId } from '@/hooks/useTenantId';
import { useToast } from '@/hooks/use-toast';
import { formatSupabaseError, invalidateRelatedQueries } from '@/lib/queryHelpers';
import { getUserFriendlyError } from '@/lib/constants/errorMessages';

// Type-safe wrapper for dynamic table names
const db = supabase as any;

export interface UseGenericCRUDOptions {
  tableName: string;
  queryKey: string;
  select?: string;
  orderBy?: {
    column: string;
    ascending?: boolean;
  };
  dependencies?: string[];
  toastMessages?: {
    create?: string;
    update?: string;
    delete?: string;
  };
  enabled?: boolean;
  staleTime?: number;
}

export interface GenericCRUDResult<T, FormData> {
  data: T[];
  isLoading: boolean;
  error: Error | null;
  createMutation: UseMutationResult<T, any, FormData, unknown>;
  updateMutation: UseMutationResult<T, any, { id: string; data: Partial<FormData> }, unknown>;
  deleteMutation: UseMutationResult<void, any, string, unknown>;
  refetch: () => Promise<void>;
}

export function useGenericCRUD<T = any, FormData = any>(
  options: UseGenericCRUDOptions
): GenericCRUDResult<T, FormData> {
  const { user } = useAuth();
  const { tenantId } = useTenantId();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    tableName,
    queryKey,
    select = '*',
    orderBy,
    dependencies = [],
    toastMessages = {},
    enabled = true,
    staleTime,
  } = options;

  // Fetch data
  const { data: rawData, isLoading, error, refetch } = useQuery({
    queryKey: [queryKey, tenantId],
    queryFn: async () => {
      if (!user || !tenantId) return [];

      const baseQuery = db.from(tableName).select(select).eq('user_id', user.id).eq('tenant_id', tenantId);
      const queryWithOrder = orderBy 
        ? baseQuery.order(orderBy.column, { ascending: orderBy.ascending ?? true })
        : baseQuery;

      const { data: resultData, error: resultError } = await queryWithOrder;

      if (resultError) throw resultError;
      return (resultData || []) as T[];
    },
    enabled: enabled && !!user && !!tenantId,
    staleTime,
  });

  // Ensure we always return an array, even if cache contains null
  const data = rawData ?? [];

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      if (!user) throw new Error('User not authenticated');
      if (!tenantId) throw new Error('Tenant not found');

      const insertQuery = db.from(tableName).insert([{
        ...(formData as any),
        user_id: user.id,
        tenant_id: tenantId,
      }]).select(select).single();

      const { data: resultData, error: resultError } = await insertQuery;

      if (resultError) throw resultError;
      return resultData as T;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      if (dependencies.length > 0) {
        invalidateRelatedQueries(queryClient, dependencies);
      }
      toast({
        title: "Success",
        description: toastMessages.create || "Item created successfully",
      });
    },
    onError: (error: any) => {
      console.error(`Error creating ${tableName}:`, error);
      toast({
        title: "Error",
        description: getUserFriendlyError(error),
        variant: "destructive",
      });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<FormData> }) => {
      if (!user) throw new Error('User not authenticated');

      const updateQuery = db.from(tableName).update(data as any).eq('id', id).eq('user_id', user.id).eq('tenant_id', tenantId).select(select).single();

      const { data: updated, error: updateError } = await updateQuery;

      if (updateError) throw updateError;
      return updated as T;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      if (dependencies.length > 0) {
        invalidateRelatedQueries(queryClient, dependencies);
      }
      toast({
        title: "Success",
        description: toastMessages.update || "Item updated successfully",
      });
    },
    onError: (error: any) => {
      console.error(`Error updating ${tableName}:`, error);
      toast({
        title: "Error",
        description: getUserFriendlyError(error),
        variant: "destructive",
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!user) throw new Error('User not authenticated');

      const deleteQuery = db.from(tableName).delete().eq('id', id).eq('user_id', user.id).eq('tenant_id', tenantId);

      const { error: deleteError } = await deleteQuery;

      if (deleteError) throw deleteError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      if (dependencies.length > 0) {
        invalidateRelatedQueries(queryClient, dependencies);
      }
      toast({
        title: "Success",
        description: toastMessages.delete || "Item deleted successfully",
      });
    },
    onError: (error: any) => {
      console.error(`Error deleting ${tableName}:`, error);
      toast({
        title: "Error",
        description: getUserFriendlyError(error),
        variant: "destructive",
      });
    },
  });

  return {
    data,
    isLoading,
    error: error as Error | null,
    createMutation,
    updateMutation,
    deleteMutation,
    refetch: async () => {
      await refetch();
    },
  };
}
